import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';

// Environment variables - using import.meta.env for Vite
const getEnvVar = (name, defaultValue = '') => {
  try {
    // Vite environment variables are available via import.meta.env
    if (import.meta.env) {
      return import.meta.env[name] || defaultValue;
    }
    return defaultValue;
  } catch (error) {
    // Fallback if import.meta is not available
    return defaultValue;
  }
};

// Check if we're in development mode
const isDevelopment = () => {
  try {
    if (import.meta.env) {
      return import.meta.env.DEV || import.meta.env.MODE === 'development';
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Firebase configuration - use environment variables or defaults
const firebaseConfig = {
  apiKey: getEnvVar('VITE_FIREBASE_API_KEY') || getEnvVar('REACT_APP_FIREBASE_API_KEY') || "demo-key",
  authDomain: getEnvVar('VITE_FIREBASE_AUTH_DOMAIN') || getEnvVar('REACT_APP_FIREBASE_AUTH_DOMAIN') || "demo-project.firebaseapp.com",
  projectId: getEnvVar('VITE_FIREBASE_PROJECT_ID') || getEnvVar('REACT_APP_FIREBASE_PROJECT_ID') || "demo-project",
  storageBucket: getEnvVar('VITE_FIREBASE_STORAGE_BUCKET') || getEnvVar('REACT_APP_FIREBASE_STORAGE_BUCKET') || "demo-project.appspot.com",
  messagingSenderId: getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID') || getEnvVar('REACT_APP_FIREBASE_MESSAGING_SENDER_ID') || "123456789",
  appId: getEnvVar('VITE_FIREBASE_APP_ID') || getEnvVar('REACT_APP_FIREBASE_APP_ID') || "1:123456789:web:abcdef123456"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
const auth = getAuth(app);

// Emulator configuration
const EMULATOR_CONFIG = {
  // Support various environment variable formats (both Vite and CRA)
  host: getEnvVar('VITE_FIREBASE_AUTH_EMULATOR_HOST') || 
        getEnvVar('REACT_APP_FIREBASE_AUTH_EMULATOR_HOST') || 
        getEnvVar('VITE_EMULATOR_HOST') || 
        getEnvVar('REACT_APP_EMULATOR_HOST') || 
        'localhost',
  port: parseInt(
    getEnvVar('VITE_FIREBASE_AUTH_EMULATOR_PORT') || 
    getEnvVar('REACT_APP_FIREBASE_AUTH_EMULATOR_PORT') || 
    getEnvVar('VITE_AUTH_EMULATOR_PORT') || 
    getEnvVar('REACT_APP_AUTH_EMULATOR_PORT') || 
    '9099'
  ),
  // Full URL override if provided
  url: getEnvVar('VITE_FIREBASE_AUTH_EMULATOR_URL') || getEnvVar('REACT_APP_FIREBASE_AUTH_EMULATOR_URL')
};

// Function to detect if we should use emulator
const shouldUseEmulator = () => {
  // Use emulator if:
  // 1. Explicitly enabled via environment variable
  // 2. In development mode AND not explicitly disabled
  // 3. Custom emulator URL is provided
  
  const forceUse = getEnvVar('VITE_USE_FIREBASE_EMULATOR') || getEnvVar('REACT_APP_USE_FIREBASE_EMULATOR');
  if (forceUse === 'true') {
    return true;
  }
  
  if (forceUse === 'false') {
    return false;
  }
  
  if (EMULATOR_CONFIG.url) {
    return true;
  }
  
  // Auto-enable in development unless explicitly disabled
  return isDevelopment();
};

// Global flag to track if emulator is already connected
let emulatorConnected = false;

// Connect to emulator if appropriate
if (shouldUseEmulator() && !emulatorConnected) {
  const emulatorUrl = EMULATOR_CONFIG.url || 
    `http://${EMULATOR_CONFIG.host}:${EMULATOR_CONFIG.port}`;
  
  try {
    connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true });
    emulatorConnected = true;
    console.log(`🔧 Connected to Firebase Auth Emulator at ${emulatorUrl}`);
  } catch (error) {
    console.warn('⚠️ Failed to connect to Firebase Auth Emulator:', error.message);
    console.warn(`   Attempted connection to: ${emulatorUrl}`);
    console.warn('   Make sure the emulator is running: firebase emulators:start --only auth');
  }
}

// Export configuration info for diagnostics
export const getEmulatorConfig = () => ({
  isUsingEmulator: shouldUseEmulator(),
  host: EMULATOR_CONFIG.host,
  port: EMULATOR_CONFIG.port,
  url: EMULATOR_CONFIG.url || `http://${EMULATOR_CONFIG.host}:${EMULATOR_CONFIG.port}`,
  projectId: firebaseConfig.projectId,
  connected: emulatorConnected
});

export { auth, app };
